import { useState, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  AppLink,
  Button,
  Callout,
  DataTable,
  Dialog,
  DialogFooter,
  DialogText,
  FailedCallout,
  RadioCards,
  ReasonField,
  SectionHeader,
  Tag,
  dataColumns,
  formatDate,
  formatDateTime,
  useReason,
  useToast,
  errorText,
  type Reason,
  useCommandMutation,
  formatBytes,
  useCsrf,
} from "@amber/ui";
import type { BackfillJobView, ExtensionPreview, HistoryWindowView } from "../contracts/index";
import { extensionPreviewQuery, historyCommands, historyQuery, AFTER_PLAN_CHANGE, ON_PLAN_CONFLICT } from "./queries";

function Bar({ value, label }: { value: number; label: string }) {
  return (
    <span className="inline-flex items-center gap-2">
      <span
        role="progressbar"
        aria-label={label}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round(value * 100)}
        className="block h-1.5 w-28 overflow-hidden rounded-full bg-surface-3"
      >
        <span className="block h-full rounded-full bg-link" style={{ width: `${String(Math.round(value * 100))}%` }} />
      </span>
      <span className="text-md text-muted">{Math.round(value * 100)}%</span>
    </span>
  );
}

function Panel({ title, children, actions }: { title: string; children: ReactNode; actions?: ReactNode }) {
  return (
    <section className="mx-4 mb-3 rounded-lg border border-divider bg-bg px-4 py-3 max-compact:mx-2" aria-label={title}>
      <div className="flex flex-wrap items-start gap-3">
        <h2 className="m-0 flex-1 text-base font-semibold">{title}</h2>
        {actions ? <div className="flex flex-wrap items-center gap-1.5">{actions}</div> : null}
      </div>
      <div className="mt-1.5 text-ui">{children}</div>
    </section>
  );
}

type ControlAction = "pause" | "resume" | "cancel";
type Control = { job: BackfillJobView; action: ControlAction };

const CONTROL_TOAST: Record<ControlAction, string> = {
  pause: "Extension paused",
  resume: "Extension resumed",
  cancel: "Extension cancelled",
};

const CONTROL_TITLE: Record<ControlAction, string> = {
  pause: "Pause extension",
  resume: "Resume extension",
  cancel: "Cancel extension",
};

function controlText(job: BackfillJobView, action: ControlAction): string {
  if (action === "pause") {
    return "No further history is read for this extension until someone resumes it; there is no automatic end. Fresh monitoring continues.";
  }
  if (action === "resume") return "The extension continues at low priority behind fresh monitoring.";
  if (job.state === "queued") return "The queued extension is removed; the history window doesn't change.";
  return `History already collected stays. The default window goes back to ${job.previous_label ?? "its previous step"}; sources keep the complete-back-to they reached.`;
}

function ControlDialog({ control, onClose }: { control: Control; onClose: () => void }) {
  const toast = useToast();
  const r = useReason();
  const { job, action } = control;
  const mutation = useCommandMutation({
    run: (csrf) => historyCommands.control(job.id, action, { reason: r.trimmed, base_revision: job.revision }, csrf),
    invalidate: AFTER_PLAN_CHANGE,
    invalidateOnConflict: ON_PLAN_CONFLICT,
    onSuccess: () => {
      toast.show(CONTROL_TOAST[action]);
      onClose();
    },
  });
  const title = CONTROL_TITLE[action];
  const text = controlText(job, action);
  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      title={title}
      onSubmit={() => {
        if (r.submit()) mutation.mutate();
      }}
      footer={
        <>
          <Button onClick={onClose}>Close</Button>
          <Button
            type="submit"
            variant={action === "cancel" ? "danger" : "primary"}
            aria-busy={mutation.isPending || undefined}
          >
            {mutation.isError ? "Try again" : title}
          </Button>
        </>
      }
    >
      <DialogText>
        Extension to <b>{job.target_label}</b>. {text}
      </DialogText>
      <ReasonField value={r.reason} onChange={r.setReason} error={r.error} />
      <FailedCallout error={mutation.error} />
    </Dialog>
  );
}

function ExtendPreviewStep({
  p,
  previewError,
  reason,
  failure,
}: {
  p: ExtensionPreview | undefined;
  previewError: Error | null;
  reason: Reason;
  failure: unknown;
}) {
  if (!p) {
    return (
      <p className="m-0 text-md text-muted">
        {previewError ? errorText(previewError) : "Working out the added interval…"}
      </p>
    );
  }
  return (
    <>
      <DialogText>{p.note}</DialogText>
      {p.queued_behind ? (
        <Callout tone="waiting" className="mt-2">
          {p.queued_behind}
        </Callout>
      ) : null}
      <dl className="mt-2 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-md">
        <dt className="text-muted">Added interval</dt>
        <dd className="m-0">
          From {p.current_label} to {p.target_label} before monitoring started
        </dd>
        <dt className="text-muted">Expected items</dt>
        <dd className="m-0">about {p.items}</dd>
        <dt className="text-muted">Estimated cost</dt>
        <dd className="m-0">
          {p.cost.currency} {p.cost.amount_decimal}
          {p.budget_share !== null ? ` · ${String(Math.round(p.budget_share * 1000) / 10)}% of the monthly budget` : ""}
        </dd>
        <dt className="text-muted">Storage growth</dt>
        <dd className="m-0">{formatBytes(p.storage_bytes)}</dd>
      </dl>
      {p.not_following.length > 0 ? (
        <Callout
          tone="info"
          className="mt-2"
          title="Not included: custom history options don't follow the default or its extensions"
        >
          <ul className="m-0 list-none p-0">
            {p.not_following.map((line) => (
              <li key={line}>{line}</li>
            ))}
          </ul>
        </Callout>
      ) : null}
      <div className="mt-3 max-h-[260px] overflow-auto rounded-lg border border-divider">
        <table className="w-full border-collapse text-md">
          <thead className="sticky top-0 bg-surface-2 text-left text-muted">
            <tr>
              <th className="px-2 py-1 font-medium">Source</th>
              <th className="px-2 py-1 font-medium">People</th>
              <th className="px-2 py-1 font-medium">Added interval</th>
              <th className="px-2 py-1 text-right font-medium">Items</th>
              <th className="px-2 py-1 text-right font-medium">Cost</th>
              <th className="px-2 py-1 text-right font-medium">Storage</th>
            </tr>
          </thead>
          <tbody>
            {p.sources.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-2 py-2 text-muted">
                  No source follows the default window yet.
                </td>
              </tr>
            ) : null}
            {p.sources.map((s) => (
              <tr key={s.source_id} className="border-t border-divider align-top">
                <td className="px-2 py-1 text-fg">
                  {s.title}
                  {(s.unreachable ?? s.skipped) ? (
                    <span className="block text-sm text-muted">{s.skipped ?? s.unreachable}</span>
                  ) : null}
                </td>
                <td className="px-2 py-1">{s.people.join(", ")}</td>
                <td className="px-2 py-1">{s.from && s.to ? `${formatDate(s.from)} – ${formatDate(s.to)}` : "—"}</td>
                <td className="px-2 py-1 text-right">{s.skipped ? "—" : s.items}</td>
                <td className="px-2 py-1 text-right">{s.skipped ? "—" : s.cost.amount_decimal}</td>
                <td className="px-2 py-1 text-right">{s.skipped ? "—" : formatBytes(s.storage_bytes)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <ReasonField value={reason.reason} onChange={reason.setReason} error={reason.error} />
      <FailedCallout error={failure} />
    </>
  );
}

function ExtendDialog({ view, onClose }: { view: HistoryWindowView; onClose: () => void }) {
  const toast = useToast();
  const csrf = useCsrf();
  const [target, setTarget] = useState(view.targets[0]?.key ?? "4_months");
  const [step, setStep] = useState<"target" | "preview">("target");
  const r = useReason();
  const preview = useQuery({ ...extensionPreviewQuery(target, csrf()), enabled: step === "preview" });
  const mutation = useCommandMutation({
    run: (token) => historyCommands.extend({ target, reason: r.trimmed }, token),
    invalidate: AFTER_PLAN_CHANGE,
    invalidateOnConflict: ON_PLAN_CONFLICT,
    onSuccess: (result) => {
      toast.show(result.state === "queued" ? "Extension queued" : "Extension started");
      onClose();
    },
  });
  const p = preview.data;
  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      title={step === "target" ? "Extend history" : `Extend history to ${p?.target_label ?? ""}`}
      wide
      onSubmit={() => {
        if (step === "target") {
          setStep("preview");
          return;
        }
        if (r.submit() && p) mutation.mutate();
      }}
      footer={
        step === "preview" ? (
          <DialogFooter
            cancel="Back"
            onClose={() => {
              setStep("target");
            }}
            confirm={p?.queued_behind ? "Queue extension" : "Start extension"}
            pending={mutation.isPending}
            failed={mutation.isError}
          />
        ) : (
          <DialogFooter onClose={onClose} confirm="Preview" pending={mutation.isPending} />
        )
      }
    >
      {step === "target" ? (
        <>
          <DialogText>
            The default window is <b>{view.window_label}</b> before monitoring started. Choose how far back to extend it
            for every person whose plan uses Default. Custom history options don&apos;t change.
          </DialogText>
          <RadioCards
            label="Extend to"
            value={target}
            options={view.targets.map((t) => ({ value: t.key, label: t.label }))}
            onChange={setTarget}
          />
        </>
      ) : (
        <ExtendPreviewStep
          p={p}
          previewError={preview.isError ? preview.error : null}
          reason={r}
          failure={mutation.error}
        />
      )}
    </Dialog>
  );
}

function timeLeftText(job: BackfillJobView): string {
  if (job.state === "paused") return "Paused";
  if (job.days_left === null) return "Estimating time left";
  return `About ${String(Math.max(1, Math.round(job.days_left)))} days left`;
}

function extendDisabledReason(view: HistoryWindowView): string | null {
  if (!view.actions.extend.enabled) return view.actions.extend.reason;
  return view.targets.length === 0 ? "The window is already at its longest step" : null;
}

function JobPanel({
  job,
  view,
  onControl,
}: {
  job: BackfillJobView;
  view: HistoryWindowView;
  onControl: (c: Control) => void;
}) {
  const disabled = view.actions.control.enabled ? null : view.actions.control.reason;
  const queued = job.state === "queued";
  return (
    <Panel
      title={
        queued
          ? `Queued: extend to ${job.target_label ?? ""}`
          : `Extension in progress: ${job.previous_label ?? ""} → ${job.target_label ?? ""}`
      }
      actions={
        <>
          {job.state === "running" ? (
            <Button
              size="sm"
              disabledReason={disabled}
              onClick={() => {
                onControl({ job, action: "pause" });
              }}
            >
              Pause…
            </Button>
          ) : null}
          {job.state === "paused" ? (
            <Button
              size="sm"
              disabledReason={disabled}
              onClick={() => {
                onControl({ job, action: "resume" });
              }}
            >
              Resume…
            </Button>
          ) : null}
          <Button
            size="sm"
            variant="danger"
            disabledReason={disabled}
            onClick={() => {
              onControl({ job, action: "cancel" });
            }}
          >
            Cancel…
          </Button>
        </>
      }
    >
      <p className="m-0 text-muted">
        Started by {job.started_by} · {formatDateTime(job.created_at)} · “{job.reason}”
      </p>
      {queued ? (
        <p className="m-0 mt-1 text-muted">
          Starts after the current extension finishes; its added interval is worked out then.
        </p>
      ) : (
        <>
          <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1">
            <b className="font-strong">
              {job.sources_complete} of {job.sources_total} source{job.sources_total === 1 ? "" : "s"} complete back to{" "}
              {job.target_label}
            </b>
            <Bar value={job.progress} label="Extension progress" />
            <span className="text-md text-muted">
              {timeLeftText(job)} · runs at low priority behind fresh monitoring
            </span>
          </div>
          {job.state === "paused" && job.last_change ? (
            <Callout tone="waiting" className="mt-2">
              Paused by {job.last_change.by} · {formatDateTime(job.last_change.at)}: {job.last_change.reason}. No
              automatic end.
            </Callout>
          ) : null}
          {job.waiting.length > 0 ? (
            <ul className="m-0 mt-2 list-none p-0 text-md text-muted">
              {job.waiting.map((w) => (
                <li key={w.source}>
                  <b className="font-strong text-fg">{w.source}</b>: {w.reason}
                </li>
              ))}
            </ul>
          ) : null}
        </>
      )}
    </Panel>
  );
}

type PersonWindow = HistoryWindowView["people"][number];

const col = dataColumns<PersonWindow>();

const PERSON_COLUMNS = [
  col.display({
    id: "person",
    header: "Person",
    meta: { width: 200 },
    cell: ({ row: { original: p } }) => (
      <AppLink href={`/profiles/${p.id}`} className="font-strong text-fg">
        {p.name}
      </AppLink>
    ),
  }),
  col.display({
    id: "option",
    header: "History option",
    meta: { width: 160 },
    cell: ({ row: { original: p } }) => (
      <span className="inline-flex items-center gap-1.5">
        {p.option_label}
        {p.custom ? <Tag tone="accent">custom</Tag> : null}
        {p.pending ? <Tag tone="waiting">Changes pending</Tag> : null}
      </span>
    ),
  }),
  col.display({
    id: "back_to",
    header: "Window back to",
    meta: { width: 130 },
    cell: ({ row: { original: p } }) => (p.back_to ? formatDate(p.back_to) : "—"),
  }),
  col.display({
    id: "complete_back_to",
    header: "Complete back to",
    meta: { width: 140 },
    cell: ({ row: { original: p } }) => (p.complete_back_to ? formatDate(p.complete_back_to) : "Not scanned yet"),
  }),
  col.display({
    id: "extension",
    header: "Extension",
    meta: { width: 140 },
    cell: ({ row: { original: p } }) =>
      p.progress
        ? `${String(p.progress.complete)} of ${String(p.progress.total)} source${p.progress.total === 1 ? "" : "s"}`
        : "—",
  }),
  col.display({
    id: "note",
    header: "Note",
    cell: ({ row: { original: p } }) => (
      <span className="text-muted">{p.note ?? (p.custom ? "Custom option; extensions don't change it" : "")}</span>
    ),
  }),
];

const PINNED = ["person"];

export function HistoryWindowSettings() {
  const query = useQuery(historyQuery);
  const [extending, setExtending] = useState(false);
  const [control, setControl] = useState<Control | null>(null);
  const view = query.data;
  return (
    <section aria-labelledby="section-title" className="flex min-h-0 flex-1 flex-col overflow-auto">
      <SectionHeader
        titleId="section-title"
        title="History window"
        lead="How far back before the start of monitoring material is collected for every tracked person. History already collected is never collected again."
        meta={
          <AppLink href="/settings/audit?subject=history_extension" className="font-strong text-link">
            Audit log
          </AppLink>
        }
        actions={
          view ? (
            <Button
              variant="primary"
              disabledReason={extendDisabledReason(view)}
              onClick={() => {
                setExtending(true);
              }}
            >
              Extend history…
            </Button>
          ) : null
        }
      />
      {query.isError ? (
        <Callout tone="attention" role="alert" className="mx-4 mb-2">
          Couldn&apos;t load the history window. {errorText(query.error)}
        </Callout>
      ) : null}
      {view ? (
        <>
          <Panel title="Default window">
            <p className="m-0">
              <b className="font-strong">{view.window_label}</b> before monitoring started, for every tracked person.
              Plans use it when their history option is Default; the other options (2 weeks, 4 months, 1 year, Since a
              date) are marked custom and don&apos;t change with the default or its extensions.
            </p>
            {view.changed ? (
              <p className="m-0 mt-1 text-md text-muted">
                Last changed by {view.changed.by} · {formatDateTime(view.changed.at)}
              </p>
            ) : null}
          </Panel>
          {view.extension ? <JobPanel job={view.extension} view={view} onControl={setControl} /> : null}
          {view.queued ? <JobPanel job={view.queued} view={view} onControl={setControl} /> : null}
          {!view.extension && !view.queued ? (
            <p className="mx-4 mt-0 mb-3 text-md text-muted max-compact:mx-2">
              No extension is running. Extending runs as low-priority backfill, in steps, with progress, pause, resume
              and cancel.
            </p>
          ) : null}
        </>
      ) : null}
      <h2 className="mx-4 mt-1 mb-1.5 text-base font-semibold max-compact:mx-2">Per person</h2>
      <DataTable
        label="History window per person"
        columns={PERSON_COLUMNS}
        rows={view?.people ?? []}
        rowId={(p) => p.id}
        minWidth={900}
        pinned={PINNED}
        isPending={query.isPending}
        skeletonRows={3}
        empty={view ? "No person has an applied plan yet." : undefined}
      />
      {view && view.recent.length > 0 ? (
        <>
          <h2 className="mx-4 mt-3 mb-1.5 text-base font-semibold max-compact:mx-2">Earlier extensions</h2>
          <ul className="mx-4 mt-0 mb-4 list-none p-0 text-md max-compact:mx-2">
            {view.recent.map((j) => (
              <li key={j.id} className="border-b border-divider py-1.5">
                <b className="font-strong">
                  {j.previous_label ?? "?"} → {j.target_label}
                </b>{" "}
                <span className="text-muted">
                  · {j.state_label} · {j.sources_complete} of {j.sources_total} source{j.sources_total === 1 ? "" : "s"}{" "}
                  · started by {j.started_by} {formatDateTime(j.created_at)}
                  {j.last_change && j.state === "cancelled"
                    ? ` · cancelled by ${j.last_change.by}: “${j.last_change.reason}”`
                    : ""}
                </span>
              </li>
            ))}
          </ul>
        </>
      ) : null}
      {extending && view ? (
        <ExtendDialog
          view={view}
          onClose={() => {
            setExtending(false);
          }}
        />
      ) : null}
      {control ? (
        <ControlDialog
          control={control}
          onClose={() => {
            setControl(null);
          }}
        />
      ) : null}
    </section>
  );
}
